import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useSuccessionTheme } from '../../SuccessionThemeProvider';
import LoadingStates from '../shared/LoadingStates';
import ConversationItem from './ConversationItem';
import { compareDates } from '../shared/dateUtils';

// GraphQL query for company information
const GET_COMPANY = gql`
  query GetCompany {
    company {
      id
      name
      deal_room_branded_name
      deal_room_branded_name_fr
      allow_blocking_in_messages
    }
  }
`;

/**
 * Helper functions
 */
const isUserOnline = (userId) => {
  // Basic presence simulation - in production this would come from GraphQL/ActionCable
  // For now, simulate that some users are online
  const onlineUserIds = ['1', '2', '3', '4', '5']; // Sample online user IDs
  return onlineUserIds.includes(String(userId));
};

const getTypingIndicatorForConversation = (conversationId) => {
  // TODO: This would be connected to real typing state from ActionCable
  // For now, return null - typing indicators will be shown in the chat panel
  // In a full implementation, this would track typing users per conversation
  return null;
};

const filterConversations = (conversations, searchQuery, currentUser, filterType = 'inbox') => {
  let filtered = conversations;
  
  // Apply filter type first
  switch (filterType) {
    case 'inbox':
      filtered = conversations.filter(conversation => {
        const isParticipant1 = String(conversation.participant1?.id) === String(currentUser?.id);
        const blockedByMe = isParticipant1
          ? conversation.blockedByParticipant1 
          : conversation.blockedByParticipant2;
        const blockedByThem = isParticipant1
          ? conversation.blockedByParticipant2 
          : conversation.blockedByParticipant1;
        const archivedByMe = isParticipant1
          ? conversation.archivedByParticipant1 
          : conversation.archivedByParticipant2;
        
        // Exclude if blocked by either party OR archived by current user
        return !blockedByMe && !blockedByThem && !archivedByMe;
      });
      break;
    case 'unread':
      filtered = conversations.filter(conversation => {
        const lastMessage = conversation.lastMessages?.[0];
        // Show conversations where:
        // 1. There is a last message
        // 2. The message is NOT read (read is false or null/undefined)
        // 3. The sender is NOT the current user (message was sent TO current user)
        const isUnread = lastMessage && 
          lastMessage.read !== true && 
          String(lastMessage.sender?.id) !== String(currentUser?.id);
        
        if (!isUnread) return false;
        
        // Also exclude blocked and archived from unread
        const blockedByMe = conversation.participant1?.id === currentUser?.id 
          ? conversation.blockedByParticipant1 
          : conversation.blockedByParticipant2;
        const blockedByThem = conversation.participant1?.id === currentUser?.id 
          ? conversation.blockedByParticipant2 
          : conversation.blockedByParticipant1;
        const archivedByMe = conversation.participant1?.id === currentUser?.id 
          ? conversation.archivedByParticipant1 
          : conversation.archivedByParticipant2;
        
        return !blockedByMe && !blockedByThem && !archivedByMe;
      });
      break;
    case 'archived':
      filtered = conversations.filter(conversation => {
        const isParticipant1 = String(conversation.participant1?.id) === String(currentUser?.id);
        const archivedByMe = isParticipant1
          ? conversation.archivedByParticipant1 
          : conversation.archivedByParticipant2;
        const blockedByMe = isParticipant1
          ? conversation.blockedByParticipant1 
          : conversation.blockedByParticipant2;
        const blockedByThem = isParticipant1
          ? conversation.blockedByParticipant2 
          : conversation.blockedByParticipant1;
        
        // Show archived conversations UNLESS they're also blocked (blocked takes priority)
        const isBlocked = blockedByMe || blockedByThem;
        return archivedByMe && !isBlocked;
      });
      break;
    case 'blocked':
      filtered = conversations.filter(conversation => {
        const isParticipant1 = String(conversation.participant1?.id) === String(currentUser?.id);
        const blockedByMe = isParticipant1
          ? conversation.blockedByParticipant1 
          : conversation.blockedByParticipant2;
        const blockedByThem = isParticipant1
          ? conversation.blockedByParticipant2 
          : conversation.blockedByParticipant1;
        return blockedByMe || blockedByThem;
      });
      break;
    default:
      filtered = conversations;
      break;
  }
  
  // Sort conversations by last message date (most recent first) using Safari-compatible comparison
  filtered = filtered.sort((a, b) => {
    const aTimestamp = a.lastMessages?.[0]?.createdAt || '1970-01-01T00:00:00Z';
    const bTimestamp = b.lastMessages?.[0]?.createdAt || '1970-01-01T00:00:00Z';
    // Use negative result to get most recent first
    return -compareDates(aTimestamp, bTimestamp);
  });
  
  // Apply search query if provided
  if (!searchQuery || !searchQuery.trim()) return filtered;
  
  const query = searchQuery.toLowerCase().trim();
  return filtered.filter(conversation => {
    // Get other participant with Paddy handling
    const otherParticipant = (() => {
      // Handle partial sale conversations - show buyer to seller, seller to buyer
      if (conversation.conversationType === 'partial_sale_listing' && conversation.partialSaleListingUser) {
        const { buyer, seller } = conversation.partialSaleListingUser;
        
        // Current user is seller, show buyer
        if (String(currentUser?.id) === String(seller?.id)) {
          return {
            id: buyer?.id,
            name: buyer?.name,
            allowed_name: buyer?.allowed_name || buyer?.name,
            profile_picture: buyer?.profile_picture || 'advisor.png',
            is_admin: false
          };
        }
        // Current user is buyer, show seller
        if (String(currentUser?.id) === String(buyer?.id)) {
          return {
            id: seller?.id,
            name: seller?.name,
            allowed_name: seller?.allowed_name || seller?.name,
            profile_picture: seller?.profile_picture || 'advisor.png',
            is_admin: false
          };
        }
      }
      
      // Handle other Paddy conversations (succession readiness, support)
      if (['paddy', 'support_request'].includes(conversation.conversationType)) {
        return {
          id: 'paddy',
          name: 'Paddy',
          allowed_name: 'Paddy',
          profile_picture: 'paddy.png',
          is_admin: false
        };
      }
      
      // Fixed: Convert both IDs to strings for comparison to avoid type mismatch
      return String(conversation.participant1?.id) === String(currentUser?.id) 
        ? conversation.participant2 
        : conversation.participant1;
    })();
    
    // Search by participant name (fuzzy matching)
    const participantName = (otherParticipant?.allowed_name || otherParticipant?.name || '').toLowerCase();
    const participantMatch = participantName.includes(query);
    
    // Search by last message content
    const lastMessage = (conversation.lastMessages?.[0]?.body || '').toLowerCase();
    const messageMatch = lastMessage.includes(query);
    
    // Search by conversation type
    const conversationType = (conversation.conversationType || '').toLowerCase();
    const typeMatch = conversationType.includes(query);
    
    // Enhanced fuzzy search: check for partial matches and word boundaries
    const words = query.split(' ').filter(word => word.length > 0);
    const wordMatch = words.some(word => 
      participantName.includes(word) || 
      lastMessage.includes(word) ||
      conversationType.includes(word)
    );
    
    // Fuzzy matching: check if characters appear in order
    const fuzzyMatch = (text, pattern) => {
      let patternIndex = 0;
      for (let i = 0; i < text.length && patternIndex < pattern.length; i++) {
        if (text[i] === pattern[patternIndex]) {
          patternIndex++;
        }
      }
      return patternIndex === pattern.length;
    };
    
    const fuzzyParticipantMatch = fuzzyMatch(participantName, query);
    const fuzzyMessageMatch = fuzzyMatch(lastMessage, query);
    
    return participantMatch || messageMatch || typeMatch || wordMatch || fuzzyParticipantMatch || fuzzyMessageMatch;
  });
};

/**
 * ConversationPanel - Beautiful conversation sidebar with succession theming
 * 
 * Features real-time conversation list with search, unread counts,
 * and smooth animations using marketplace colors.
 */
const ConversationPanel = ({
  currentUser,
  selectedConversationId,
  onConversationSelect,
  searchQuery,
  theme,
  conversations = [],
  loading = false,
  error = null,
  onRefresh = () => {}
}) => {
  const intl = useIntl();
  const { theme: successionTheme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('inbox'); // inbox, unread, archived, blocked

  // Fetch company data for deal_room_branded_name
  const { data: companyData } = useQuery(GET_COMPANY, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-first'
  });

  // Use search query from parent or local state
  const effectiveSearchQuery = searchQuery || localSearchQuery;

  return (
    <div className="h-full flex flex-col bg-[var(--succession-bg-primary)] overflow-hidden">
      
      {/* Search & Filter section - Demo clean style */}
      {!searchQuery && (
        <div className="flex-shrink-0 p-4 border-b border-[var(--succession-border)] bg-[var(--succession-bg-primary)]">
          {/* Search Input - Demo style with icon prefix */}
          <div className="flex items-center mb-4 bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg overflow-hidden focus-within:border-[#4A90E2] focus-within:ring-2 focus-within:ring-[#4A90E2]/20 transition-all">
            <div className="flex items-center justify-center pl-3 pr-2">
              <svg className="w-4 h-4 text-[var(--succession-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              placeholder={intl.formatMessage({ id: 'succession.messaging.conversation_panel.filter_placeholder' })}
              className="flex-1 py-2.5 pr-4 bg-transparent text-sm text-[var(--succession-text-primary)] placeholder-[var(--succession-text-muted)] outline-none border-none"
            />
          </div>

          {/* Filter Tabs - Demo style with underline indicator */}
          {(() => {
            // Calculate unread count for badge
            const unreadCount = conversations.filter(conversation => {
              const lastMessage = conversation.lastMessages?.[0];
              return lastMessage && 
                lastMessage.read !== true && 
                String(lastMessage.sender?.id) !== String(currentUser?.id);
            }).length;
            
            return (
              <div className="flex bg-[var(--succession-bg-tertiary)] rounded-lg p-1">
                {[
                  { key: 'inbox', label: intl.formatMessage({ id: 'succession.messaging.conversation_panel.tab_inbox' }), count: null },
                  { key: 'unread', label: intl.formatMessage({ id: 'succession.messaging.conversation_panel.tab_unread' }), count: unreadCount },
                  { key: 'archived', label: intl.formatMessage({ id: 'succession.messaging.conversation_panel.tab_archived' }), count: null },
                  ...(companyData?.company?.allow_blocking_in_messages ? [{ key: 'blocked', label: intl.formatMessage({ id: 'succession.messaging.conversation_panel.tab_blocked' }), count: null }] : [])
                ].map(filter => (
                  <button
                    key={filter.key}
                    onClick={() => setFilterType(filter.key)}
                    className={`flex-1 py-2 px-3 text-sm font-medium text-center transition-all rounded-md flex items-center justify-center gap-1.5 ${
                      filterType === filter.key 
                        ? 'bg-[var(--succession-bg-primary)] text-[var(--succession-primary)] font-semibold shadow-sm' 
                        : 'text-[var(--succession-text-secondary)] hover:text-[var(--succession-text-primary)] hover:bg-[var(--succession-hover-bg)]'
                    }`}
                  >
                    {filter.label}
                    {filter.count > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 text-xs font-semibold rounded-full flex items-center justify-center bg-[var(--succession-primary)] text-white">
                        {filter.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Conversations List - Demo clean style */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden bg-[var(--succession-bg-primary)]">
        {(() => {
          if (loading) return <LoadingStates.ConversationList />;
          if (error) return <ErrorMessage error={error} onRetry={onRefresh} />;
          
          const filteredConversations = filterConversations(conversations, effectiveSearchQuery, currentUser, filterType);
          
          if (filteredConversations.length === 0) {
            return <EmptyConversations hasSearch={!!effectiveSearchQuery} searchQuery={effectiveSearchQuery} />;
          }
          
          return (
            <div>
              {filteredConversations.map(conversation => {
                // Get other participant
                const otherParticipant = (() => {
                  // Handle partial sale conversations - show buyer to seller, seller to buyer
                  if (conversation.conversationType === 'partial_sale_listing' && conversation.partialSaleListingUser) {
                    const { buyer, seller } = conversation.partialSaleListingUser;
                    
                    // Current user is seller, show buyer
                    if (String(currentUser?.id) === String(seller?.id)) {
                      return {
                        id: buyer?.id,
                        name: buyer?.name,
                        allowed_name: buyer?.allowed_name || buyer?.name,
                        user_name: buyer?.user_name,
                        fb_user_name: buyer?.fb_user_name,
                        profile_picture: buyer?.profile_picture || 'advisor.png',
                        is_admin: false
                      };
                    }
                    // Current user is buyer, show seller
                    if (String(currentUser?.id) === String(buyer?.id)) {
                      return {
                        id: seller?.id,
                        name: seller?.name,
                        allowed_name: seller?.allowed_name || seller?.name,
                        user_name: seller?.user_name,
                        fb_user_name: seller?.fb_user_name,
                        profile_picture: seller?.profile_picture || 'advisor.png',
                        is_admin: false
                      };
                    }
                  }

                  // Handle other Paddy conversations (succession readiness, support)
                  if (['paddy', 'support_request'].includes(conversation.conversationType)) {
                    return {
                      id: 'paddy',
                      name: 'Paddy',
                      allowed_name: 'Paddy',
                      profile_picture: 'paddy.png',
                      is_admin: false
                    };
                  }
                  
                  // Fixed: Convert both IDs to strings for comparison to avoid type mismatch
                  return String(conversation.participant1?.id) === String(currentUser?.id) 
                    ? conversation.participant2 
                    : conversation.participant1;
                })();
                
                return (
                  <ConversationItem
                    key={conversation.id}
                    conversation={{
                      ...conversation,
                      unread_count: conversation.lastMessages?.[0] && 
                        conversation.lastMessages[0].read !== true && 
                        String(conversation.lastMessages[0].sender?.id) !== String(currentUser?.id) ? 1 : 0,
                      last_message: conversation.lastMessages?.[0] ? {
                        ...conversation.lastMessages[0],
                        created_at: conversation.lastMessages[0].createdAt
                      } : null,
                      blocked_by_me: conversation.participant1?.id === currentUser?.id ? conversation.blockedByParticipant1 : conversation.blockedByParticipant2,
                      blocked_by_them: conversation.participant1?.id === currentUser?.id ? conversation.blockedByParticipant2 : conversation.blockedByParticipant1,
                      conversation_type: conversation.conversationType,
                      company: conversation.company || companyData?.company || null
                    }}
                    currentUser={currentUser}
                    otherParticipant={otherParticipant}
                    isSelected={selectedConversationId === conversation.id}
                    isOnline={isUserOnline(otherParticipant?.id)}
                    typingIndicator={getTypingIndicatorForConversation(conversation.id)}
                    onClick={() => onConversationSelect(conversation.id)}
                  />
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

/**
 * Error and Empty States - Demo clean style
 */
const ErrorMessage = ({ error, onRetry }) => {
  const intl = useIntl();
  return (
    <div className="p-8 text-center">
      <div className="text-red-500 text-sm mb-2">{intl.formatMessage({ id: 'succession.messaging.conversation_panel.failed_to_load' })}</div>
      <button
        onClick={onRetry}
        className="text-[#4A90E2] text-sm hover:underline bg-transparent border-none cursor-pointer"
      >
        {intl.formatMessage({ id: 'succession.messaging.conversation_panel.try_again' })}
      </button>
    </div>
  );
};

const EmptyConversations = ({ hasSearch, searchQuery }) => {
  const intl = useIntl();
  return (
    <div className="p-8 text-center">
      <div className="w-16 h-16 bg-[var(--succession-bg-secondary)] rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-[var(--succession-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      </div>
      <h3 className="text-sm font-medium text-[var(--succession-text-primary)] mb-2">
        {hasSearch ? intl.formatMessage({ id: 'succession.messaging.conversation_panel.no_conversations_found' }) : intl.formatMessage({ id: 'succession.messaging.conversation_panel.no_conversations' })}
      </h3>
      <p className="text-sm text-[var(--succession-text-secondary)]">
        {hasSearch ? intl.formatMessage({ id: 'succession.messaging.conversation_panel.no_results_subtitle' }) : intl.formatMessage({ id: 'succession.messaging.conversation_panel.no_conversations_subtitle' })}
      </p>
    </div>
  );
};

ConversationPanel.propTypes = {
  currentUser: PropTypes.object,
  selectedConversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onConversationSelect: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  theme: PropTypes.string.isRequired,
  conversations: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.object,
  onRefresh: PropTypes.func,
};

export default ConversationPanel; 